import { ReactElement } from "react";
import { IDelayBase, IDelayMeltShop, IDelayQMOS } from "../../../types";
import { getPalette } from "../../../configuration/configuration";
import KeyValueItem from "../../../components/charts/keyValueItem";
import { formatNumber, formatTimeWithSeconds } from "../../../system/utils";
import HtmlTooltip from "../../../components/charts/tooltip";

interface IProps {
  chartKey: string;
  delays: IDelayBase[];
  selected?: IDelayBase | null;
  setSelected?: (row: IDelayBase | null) => void;
  y: number;
  height: number;
  xAccessorScaled: (delay: IDelayBase) => number;
  widthAccessorScaled: (delay: IDelayBase) => number;
}

const tooltipDetail = (delay: IDelayBase): ReactElement => {

  if (delay.__typename === "IDelayQMOS") {
    const delayQMOS = delay as IDelayQMOS;

    return (
      <>
        <KeyValueItem name={"Area Group"} value={delayQMOS.areaGroup?.name ?? "---"}/>
        <KeyValueItem name={"Area"} value={delayQMOS.area?.name ?? "---"}/>
        <KeyValueItem name={"Equipment"} value={delayQMOS.equipment?.name ?? "---"}/>
        <KeyValueItem name={"Observation"} value={delayQMOS.observation?.name ?? "---"}/>
        <KeyValueItem name={"Reason"} value={delayQMOS.reason?.name ?? "---"}/>
        <KeyValueItem name={"Responsibility"} value={delayQMOS.responsibility?.name ?? "---"}/>
        <KeyValueItem name={"Action Code"} value={delayQMOS.actionCode?.name ?? "---"}/>
      </>
    );
  }

  const meltShop = delay as IDelayMeltShop;

  return (
    <KeyValueItem name={"Category"} value={meltShop.category.name}/>
  );
};

const DelayLayerGantt = ({ chartKey, delays, height, y, xAccessorScaled, widthAccessorScaled, selected, setSelected }: IProps):  ReactElement => {

  const onSelectedDelay = (delay: IDelayBase) => {
    if (setSelected) {
      setSelected(delay !== selected ? delay : null);
    }
  };

  return (
    <>
      {
        delays.map((item: IDelayBase, delayIndex: number) => {
          const delayKey = `${chartKey}-delay-${delayIndex}`;
          const duration = item.endDatetime ? formatNumber((item.endDatetime - item.beginDatetime) / 60, 2) : "---";
          let fill = item.isDownDay
            ? getPalette().statusColor.scheduleDay.background
            : item.delayType === 0
              ? getPalette().statusColor.delay.background
              : getPalette().statusColor.deepOrange.background;

          if(item === selected){
            fill = getPalette().statusColor.selected.background;
          }

          return (
            <g key={delayKey}>
              <HtmlTooltip title={
                <div>
                  <div className="display-flex-column align-items-center">
                    <strong>Delay</strong>
                    {item.isDownDay && <strong>Scheduled Delay</strong>}
                    <strong>{item.production?.reference}</strong>
                  </div>
                  <KeyValueItem name={"Begin"} value={formatTimeWithSeconds(item.beginDatetime as number)}/>
                  <KeyValueItem name={"End"} value={formatTimeWithSeconds(item.endDatetime as number)}/>
                  <KeyValueItem name={"Duration"} value={duration} unit="min"/>
                  {tooltipDetail(item)}
                </div>
              }>
                <rect
                  cursor="pointer"
                  fill={fill}
                  x={xAccessorScaled(item)}
                  y={y}
                  width={Math.max(widthAccessorScaled(item), 0)}
                  height={height}
                  onClick={() => onSelectedDelay(item)}>
                  {!item.endDatetime &&
                    <animate attributeName="opacity" dur="2s" keyTimes="0;0.5;1" values="0.5;1;0.5" repeatCount="indefinite"/>
                  }
                </rect>
              </HtmlTooltip>
            </g>
          );
        })
      }
    </>);

};

export default DelayLayerGantt;
